import React from 'react';
import * as linkifyjs from 'linkifyjs';
import ReactMarkdown, {type ReactMarkdownProps} from 'react-markdown';

import Link from 'web-app/react/components/link/link';

const getLinkRenderer = (onClick?: (e: any) => void, showExternalLinkWarning?: boolean) => {
    const LinkRenderer: React.FC<React.PropsWithChildren<{href: string}>> = ({href, children}) => (
        <Link autoAdjustHref={true} href={href} onClick={onClick} showExternalLinkWarning={showExternalLinkWarning}>
            {children}
        </Link>
    );

    return LinkRenderer;
};

const ListRenderer: React.FC<React.PropsWithChildren<{ordered: boolean; start: number}>> = ({
    children,
    ordered,
    start,
}) => {
    if (ordered) {
        return <ol start={start}>{children}</ol>;
    } else {
        return <ul className="default">{children}</ul>;
    }
};

const StrippedLink: React.FC<React.PropsWithChildren<unknown>> = ({children}) => <span>{children}</span>;

const linkifyMarkdown = (source: string): string => {
    const links = linkifyjs.find(source);
    return links.reduce((reduction, link) => {
        return reduction.replace(link.value, `[${link.value}](${link.href})`);
    }, source);
};

interface Props {
    renderers?: ReactMarkdownProps['renderers'];
    onLinkClick?: (e: any) => void;
    disableLinks?: boolean;
    skipHtml?: boolean;
    source: string;
    className?: string;
    linkify?: boolean;
    showExternalLinkWarning?: boolean;
}

const Markdown: React.FC<React.PropsWithChildren<Props>> = props => {
    const {onLinkClick, disableLinks, linkify, source, skipHtml, showExternalLinkWarning, ...otherProps} = props;
    const renderers = props.renderers || {};
    const linkRenderer = disableLinks ? StrippedLink : getLinkRenderer(onLinkClick, showExternalLinkWarning);

    const computedSource = React.useMemo(() => {
        return linkify ? linkifyMarkdown(source) : source;
    }, [linkify, source]);

    return (
        <ReactMarkdown
            {...otherProps}
            skipHtml={skipHtml ?? true}
            renderers={{
                link: linkRenderer,
                list: ListRenderer,
                ...renderers,
            }}
            children={computedSource}
        />
    );
};

export default Markdown;
